/**
 * PadLab's structured local store.
 *
 * Everything the app knows lives here except the audio bytes themselves, which are far too
 * large to sit in a structured record and go to `blob-store.ts` instead. This module owns
 * the schema, the upgrade ladder, and the one transaction primitive the rest of the app is
 * allowed to use.
 *
 * Three decisions shape the file:
 *
 * 1. **A project is written as a new record plus a pointer flip, in one transaction.**
 *    IndexedDB commits a transaction as a unit, so a tab that dies mid-save leaves the
 *    previous document and the previous pointer intact. There is no window in which
 *    `projects.currentRevision` names a half-written document. This is the web equivalent of
 *    the native store's write-temp-then-rename.
 * 2. **Revision metadata and revision documents are separate stores.** Listing version
 *    history must not deserialise twenty copies of a project; the metadata store is small
 *    enough to read whole, and the document is fetched only when a version is restored.
 * 3. **IndexedDB may simply not be there.** Some embedded and locked-down WebKit contexts
 *    refuse `indexedDB.open` outright. Rather than leave the user with an app that cannot
 *    open a project, the module falls back to an in-memory backend with identical
 *    semantics, reports `backend === 'memory'`, and lets the UI say plainly that work will
 *    not survive a reload. Feature-detected, never assumed.
 *
 * A note on `await` inside `transact`: an IndexedDB transaction commits as soon as the
 * microtask queue drains with no request outstanding. Awaiting anything that is not a call
 * on this transaction — a fetch, a blob read, a timer — ends it. The body must only await
 * store calls; anything else belongs before or after.
 */

import { createLogger, idPrefix } from '../core/log';
import type { JsonObject } from '../model/schema';
import type { ISODate, UUID } from '../model/types';

const log = createLogger('db');

export const DATABASE_NAME = 'padlab';

/** Bump only alongside a new rung in `applyUpgrade`. */
export const DATABASE_VERSION = 1;

// MARK: - Errors

export type StorageErrorKind =
  /** The origin is out of space, or the browser refused the write on a quota heuristic. */
  | 'quotaExceeded'
  /** Another tab holds the database open at an older version. */
  | 'blocked'
  /** IndexedDB is missing or refused to open. The memory backend is in use. */
  | 'unavailable'
  /** The transaction was aborted by the browser rather than by us. */
  | 'aborted'
  /** A record exists but does not hold what its type says it holds. */
  | 'corrupt'
  | 'notFound';

/**
 * Every storage failure carries a sentence the user can read and one telling them what to
 * do about it — the same contract as the native `PersistenceError`.
 */
export class StorageError extends Error {
  readonly kind: StorageErrorKind;
  readonly recoverySuggestion: string;

  constructor(kind: StorageErrorKind, message: string, recoverySuggestion: string, cause?: unknown) {
    super(message);
    this.name = 'StorageError';
    this.kind = kind;
    this.recoverySuggestion = recoverySuggestion;
    if (cause !== undefined) this.cause = cause;
  }

  static quotaExceeded(needed?: number, available?: number, cause?: unknown): StorageError {
    const detail =
      needed !== undefined && needed > 0 && available !== undefined && available > 0
        ? `Not enough space. This needs ${formatBytes(needed)} and ${formatBytes(available)} is free.`
        : 'There is not enough space left on this device to save that.';
    return new StorageError(
      'quotaExceeded',
      detail,
      'Open Storage in PadLab’s settings and clear waveform caches and bounced renders. That never touches your recordings. If space is still tight, remove some sounds.',
      cause,
    );
  }

  static unavailable(cause?: unknown): StorageError {
    return new StorageError(
      'unavailable',
      'This browser is not letting PadLab save anything to the device.',
      'Turn off Private Browsing, or allow website data for this site, then reload. Until then PadLab keeps your work in memory only and it will be lost when you close the tab.',
      cause,
    );
  }

  static blocked(): StorageError {
    return new StorageError(
      'blocked',
      'Another PadLab tab is holding on to your projects.',
      'Close the other PadLab tabs and reload this one.',
    );
  }

  static aborted(cause?: unknown): StorageError {
    return new StorageError(
      'aborted',
      'That change could not be written to the device.',
      'Try again. If it keeps happening, reload PadLab. Your last saved project is untouched.',
      cause,
    );
  }

  static corrupt(what: string): StorageError {
    return new StorageError(
      'corrupt',
      `${what} is damaged and could not be read.`,
      'PadLab keeps a previous copy and recent snapshots. Try Recover Version from the project menu.',
    );
  }

  static notFound(what: string): StorageError {
    return new StorageError('notFound', `${what} could not be found.`, 'It may have been deleted from another tab. Reload PadLab to refresh the list.');
  }
}

export function isStorageError(value: unknown): value is StorageError {
  return value instanceof StorageError;
}

/**
 * Quota failures surface with several different names depending on the browser and the age
 * of the implementation, and Safari has historically thrown a bare `UnknownError` when the
 * disk fills mid-transaction. Anything that smells like space is treated as space, because
 * the recovery advice is the same and a wrong "try again" is worse than a wrong "free some
 * space".
 */
export function isQuotaError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  // The legacy numeric code, still emitted by older WebKit.
  const code = (error as { code?: number }).code;
  if (code === 22 || code === 1014) return true;
  return /quota|storage (is )?full|not enough space|disk/i.test(error.message);
}

/**
 * Byte counts in the same voice as `ByteCountFormatter.string(countStyle: .file)`, which is
 * what the iPhone app's error messages are written with: decimal units, one decimal place,
 * and no trailing `.0`. The same file rejected on both platforms then quotes the same size.
 */
export function formatBytes(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return 'Zero KB';
  if (count < 1000) return `${Math.round(count)} bytes`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = count / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} ${units[unit] ?? 'KB'}`;
}

// MARK: - Records

/**
 * The pointer record. Holds everything the project list needs so opening the home screen
 * never deserialises a project document, plus the two revision keys that make a save atomic.
 */
export interface ProjectRecord {
  id: UUID;
  /** Monotonic per project; the suffix of every revision key. */
  sequence: number;
  /** Key of the document that *is* the project right now. */
  currentRevision: string;
  /** The document `currentRevision` replaced. Kept as the crash backup, like `project.json.bak`. */
  previousRevision?: string;
  name: string;
  createdAt: ISODate;
  modifiedAt: ISODate;
  padCount: number;
  assetCount: number;
  tempo: number;
  artworkFileName?: string;
  /** Encoded size of the project document, for the storage screen. */
  byteSize: number;
  isArchived: boolean;
  /** Present only while the project is in the trash; the index skips records without it. */
  deletedAt?: ISODate;
  lastOpenedAt?: ISODate;
  /** Drives the five-minute minimum interval between automatic snapshots. */
  lastSnapshotAt?: ISODate;
}

export interface RevisionRecord {
  /** `${projectID}:${sequence}` — sortable within a project and unique across the store. */
  key: string;
  projectID: UUID;
  sequence: number;
  createdAt: ISODate;
  byteSize: number;
  /** Snapshots survive pruning; ordinary revisions live only until the next save. */
  isSnapshot: boolean;
  isAutomatic: boolean;
  label?: string;
}

export interface RevisionDocumentRecord {
  key: string;
  /**
   * The project as canonical `.padlab` JSON text — sorted keys, second-resolution UTC dates,
   * exactly what the iPhone app writes.
   *
   * Text rather than a cloned object for two reasons: the bytes handed to a package export
   * are then the stored bytes, with no re-encode that could drift from what was saved; and
   * structured-cloning a deep object graph on every autosave costs more than copying a
   * string, which for a pattern-heavy project is the difference between an imperceptible
   * save and a visible one.
   */
  text: string;
}

/**
 * Asset metadata mirrored out of the project document. The copy exists so an import can ask
 * "have I already got these bytes?" without deserialising a project, and so the storage
 * screen can account for media without opening one either.
 */
export interface AssetRecord {
  /** `${projectID}.${assetID}` — also the blob-store key for the audio itself. */
  key: string;
  projectID: UUID;
  assetID: UUID;
  name: string;
  fileName: string;
  contentHash: string;
  origin: string;
  createdAt: ISODate;
  duration: number;
  sampleRate: number;
  channelCount: number;
  frameCount: number;
  byteSize: number;
}

/**
 * The blob-store key for an asset's audio. Project-scoped because two projects may hold the
 * same asset id — duplicating a project keeps its asset ids so pads, patterns and scenes
 * still point at their sounds, exactly as the native store does.
 */
export function assetKey(projectID: UUID, assetID: UUID): string {
  return `${projectID}.${assetID}`;
}

/** Blob-store key for a take that is being recorded, or was interrupted mid-recording. */
export function takeKey(recordingID: UUID): string {
  return `take-${recordingID}`;
}

/** Blob-store key for a bounced or exported render, which is always safe to delete. */
export function renderKey(projectID: UUID, renderID: string): string {
  return `render-${projectID}-${renderID}`;
}

/** Blob-store key for audio installed with a downloaded sound pack. */
export function packAssetKey(packID: string, assetID: string): string {
  return `pack-${packID}-${assetID}`;
}

/**
 * The tag every generated library sound carries.
 *
 * Declared here, in the storage layer, because it decides *where a sound's bytes live* and
 * not merely how they are labelled.
 */
export const LIBRARY_ASSET_TAG = 'padlab library';

/**
 * Blob-store key for a sound from the generated library.
 *
 * Deliberately not scoped to a project. The library is three hundred sounds and eighty
 * megabytes, it is identical on every device because it is synthesised from a fixed
 * catalogue, and it used to be written once *per project* — so a second project cost another
 * eighty megabytes of byte-identical audio on the platform where storage pressure is the
 * thing most likely to lose someone their work. Library asset ids are derived from the
 * catalogue entry, so the same sound resolves to the same key everywhere.
 */
export function libraryAssetKey(assetID: UUID): string {
  return `library-${assetID}`;
}

/** Where an asset's bytes are, whichever collection it belongs to. */
export function mediaKeyForAsset(
  projectID: UUID,
  asset: { readonly id: UUID; readonly tags: readonly string[] },
): string {
  return asset.tags.includes(LIBRARY_ASSET_TAG) ? libraryAssetKey(asset.id) : assetKey(projectID, asset.id);
}

/** Blob-store key for a picture a pad or a project wears. Never regenerable. */
export function artworkKey(projectID: UUID, artworkID: string): string {
  return `artwork-${projectID}-${artworkID}`;
}

export type BlobCategory = 'media' | 'take' | 'render' | 'pack' | 'artwork';

/**
 * Which bucket a blob belongs to, from its key alone. UUIDs begin with a hex digit, so a
 * media key can never be mistaken for one of the prefixed namespaces.
 */
export function blobCategory(id: string): BlobCategory {
  if (id.startsWith('take-')) return 'take';
  if (id.startsWith('render-')) return 'render';
  if (id.startsWith('pack-')) return 'pack';
  if (id.startsWith('artwork-')) return 'artwork';
  // The generated library is shared, not regenerable-on-demand: clearing it would silence
  // every pad using one of its sounds until a three-hundred-sound rebuild finished.
  if (id.startsWith('library-')) return 'media';
  return 'media';
}

export interface PeaksRecord {
  /** Matches `AssetRecord.key`. */
  key: string;
  projectID: UUID;
  assetID: UUID;
  /** Format version of this cache entry; a mismatch is a miss, never a repair. */
  version: number;
  peaksPerSecond: number;
  duration: number;
  bucketCount: number;
  minima: Float32Array;
  maxima: Float32Array;
  computedAt: ISODate;
  byteSize: number;
}

export interface SettingRecord {
  key: string;
  value: unknown;
}

export interface PackRecord {
  id: string;
  name: string;
  creator: string;
  version: number;
  installedAt: ISODate;
  byteSize: number;
  /** Blob-store keys owned by this pack, so uninstalling reclaims exactly its bytes. */
  blobKeys: string[];
  manifest: JsonObject;
}

export type RecordingState = 'recording' | 'finished' | 'abandoned';

/**
 * A take, registered *before* its first chunk is written. A row still in `recording` at
 * launch is a session the browser killed — a tab discard, a crash, a phone call — and its
 * audio is offered back to the user rather than silently swept up.
 */
export interface RecordingRecord {
  id: UUID;
  projectID: UUID;
  state: RecordingState;
  startedAt: ISODate;
  updatedAt: ISODate;
  /** Blob-store key holding the take's bytes. */
  blobKey: string;
  mimeType: string;
  fileExtension: string;
  sampleRate: number;
  channelCount: number;
  duration: number;
  byteSize: number;
  chunkCount: number;
  peakLevel: number;
  name?: string;
}

export type BlobBackendName = 'opfs' | 'indexeddb' | 'memory';

/** The blob index: one row per stored object, whichever backend holds the bytes. */
export interface BlobRecord {
  id: string;
  byteSize: number;
  mimeType: string;
  chunkCount: number;
  backend: BlobBackendName;
  updatedAt: ISODate;
}

export interface BlobChunkRecord {
  id: string;
  index: number;
  data: Blob;
}

export interface StoreValueMap {
  projects: ProjectRecord;
  revisions: RevisionRecord;
  revisionDocuments: RevisionDocumentRecord;
  assets: AssetRecord;
  peaks: PeaksRecord;
  settings: SettingRecord;
  packs: PackRecord;
  recordings: RecordingRecord;
  blobs: BlobRecord;
  blobChunks: BlobChunkRecord;
}

export type StoreName = keyof StoreValueMap;

// MARK: - Schema

interface IndexSpec {
  readonly name: string;
  readonly keyPath: string | readonly string[];
}

interface StoreSpec {
  readonly name: StoreName;
  readonly keyPath: string | readonly string[];
  readonly indexes: readonly IndexSpec[];
}

/**
 * One description of the schema, used by both backends: IndexedDB builds real object stores
 * from it during an upgrade, and the memory backend evaluates index lookups against it.
 */
const SCHEMA: readonly StoreSpec[] = [
  {
    name: 'projects',
    keyPath: 'id',
    indexes: [
      { name: 'by-modified', keyPath: 'modifiedAt' },
      // Sparse by construction: IndexedDB skips records whose key path is undefined, so this
      // index contains exactly the trashed projects.
      { name: 'by-deleted', keyPath: 'deletedAt' },
    ],
  },
  {
    name: 'revisions',
    keyPath: 'key',
    indexes: [
      { name: 'by-project', keyPath: 'projectID' },
      { name: 'by-project-sequence', keyPath: ['projectID', 'sequence'] },
    ],
  },
  { name: 'revisionDocuments', keyPath: 'key', indexes: [] },
  {
    name: 'assets',
    keyPath: 'key',
    indexes: [
      { name: 'by-project', keyPath: 'projectID' },
      { name: 'by-hash', keyPath: 'contentHash' },
      { name: 'by-project-hash', keyPath: ['projectID', 'contentHash'] },
    ],
  },
  { name: 'peaks', keyPath: 'key', indexes: [{ name: 'by-project', keyPath: 'projectID' }] },
  { name: 'settings', keyPath: 'key', indexes: [] },
  { name: 'packs', keyPath: 'id', indexes: [] },
  {
    name: 'recordings',
    keyPath: 'id',
    indexes: [
      { name: 'by-project', keyPath: 'projectID' },
      { name: 'by-state', keyPath: 'state' },
    ],
  },
  { name: 'blobs', keyPath: 'id', indexes: [] },
  { name: 'blobChunks', keyPath: ['id', 'index'], indexes: [{ name: 'by-id', keyPath: 'id' }] },
];

/**
 * The upgrade ladder. `oldVersion` is 0 on a first run, so the `case 0` rung is what builds
 * the schema; a future version 2 adds `case 1:` above the `default` and nothing else moves.
 */
function applyUpgrade(database: IDBDatabase, oldVersion: number): void {
  // An if-ladder rather than a switch, because every rung has to run on a fresh database as
  // well as on the version below it — which a switch can only express by falling through.
  if (oldVersion < 1) {
    for (const spec of SCHEMA) {
      const store = database.createObjectStore(spec.name, { keyPath: spec.keyPath as string | string[] });
      for (const index of spec.indexes) {
        store.createIndex(index.name, index.keyPath as string | string[]);
      }
    }
  }
}

// MARK: - Key queries

export interface KeyRangeSpec {
  readonly lower?: IDBValidKey;
  readonly upper?: IDBValidKey;
  readonly lowerOpen?: boolean;
  readonly upperOpen?: boolean;
}

export type KeyQuery = IDBValidKey | KeyRangeSpec | null | undefined;

function isRangeSpec(value: KeyQuery): value is KeyRangeSpec {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    ('lower' in value || 'upper' in value)
  );
}

/**
 * Range constructors that do not touch `IDBKeyRange`. The memory backend has to work in
 * contexts where IndexedDB is absent entirely, and `IDBKeyRange` is absent with it.
 */
export const keyRange = {
  only(value: IDBValidKey): KeyRangeSpec {
    return { lower: value, upper: value };
  },
  bound(lower: IDBValidKey, upper: IDBValidKey, lowerOpen = false, upperOpen = false): KeyRangeSpec {
    return { lower, upper, lowerOpen, upperOpen };
  },
  lowerBound(lower: IDBValidKey, open = false): KeyRangeSpec {
    return { lower, lowerOpen: open };
  },
  upperBound(upper: IDBValidKey, open = false): KeyRangeSpec {
    return { upper, upperOpen: open };
  },
  /** Every key beginning with `prefix`. `￿` sorts above any character we ever store. */
  prefix(prefix: string): KeyRangeSpec {
    return { lower: prefix, upper: `${prefix}￿` };
  },
  /** Every compound key whose first element is `value`. */
  compoundPrefix(value: IDBValidKey): KeyRangeSpec {
    return { lower: [value], upper: [value, []] };
  },
};

function toIDBQuery(query: KeyQuery): IDBValidKey | IDBKeyRange | undefined {
  if (query === null || query === undefined) return undefined;
  if (!isRangeSpec(query)) return query;
  const { lower, upper, lowerOpen = false, upperOpen = false } = query;
  if (lower !== undefined && upper !== undefined) {
    return IDBKeyRange.bound(lower, upper, lowerOpen, upperOpen);
  }
  if (lower !== undefined) return IDBKeyRange.lowerBound(lower, lowerOpen);
  if (upper !== undefined) return IDBKeyRange.upperBound(upper, upperOpen);
  return undefined;
}

/** IndexedDB key ordering, restricted to the key shapes this schema actually uses. */
function compareKeys(a: IDBValidKey, b: IDBValidKey): number {
  const rank = (value: IDBValidKey): number => (typeof value === 'number' ? 0 : typeof value === 'string' ? 1 : 2);
  const rankA = rank(a);
  const rankB = rank(b);
  if (rankA !== rankB) return rankA - rankB;
  if (rankA === 0) return (a as number) - (b as number);
  if (rankA === 1) return (a as string) < (b as string) ? -1 : (a as string) > (b as string) ? 1 : 0;
  const arrayA = a as IDBValidKey[];
  const arrayB = b as IDBValidKey[];
  const shared = Math.min(arrayA.length, arrayB.length);
  for (let index = 0; index < shared; index += 1) {
    const left = arrayA[index];
    const right = arrayB[index];
    if (left === undefined || right === undefined) break;
    const order = compareKeys(left, right);
    if (order !== 0) return order;
  }
  return arrayA.length - arrayB.length;
}

function matchesQuery(key: IDBValidKey, query: KeyQuery): boolean {
  if (query === null || query === undefined) return true;
  if (!isRangeSpec(query)) return compareKeys(key, query) === 0;
  if (query.lower !== undefined) {
    const order = compareKeys(key, query.lower);
    if (order < 0 || (order === 0 && query.lowerOpen === true)) return false;
  }
  if (query.upper !== undefined) {
    const order = compareKeys(key, query.upper);
    if (order > 0 || (order === 0 && query.upperOpen === true)) return false;
  }
  return true;
}

function extractKey(value: unknown, keyPath: string | readonly string[]): IDBValidKey | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const source = value as Record<string, unknown>;
  if (typeof keyPath === 'string') {
    const part = source[keyPath];
    return typeof part === 'string' || typeof part === 'number' ? part : undefined;
  }
  const parts: IDBValidKey[] = [];
  for (const segment of keyPath) {
    const part = source[segment];
    if (typeof part !== 'string' && typeof part !== 'number') return undefined;
    parts.push(part);
  }
  return parts;
}

/** Stable string form of a key, used only as a `Map` index inside the memory backend. */
function keyToken(key: IDBValidKey): string {
  return Array.isArray(key) ? key.map((part) => `${typeof part}:${String(part)}`).join(' ') : `${typeof key}:${String(key)}`;
}

// MARK: - Store access

export interface IndexAccess<V> {
  getAll(query?: KeyQuery, count?: number): Promise<V[]>;
  getAllKeys(query?: KeyQuery, count?: number): Promise<IDBValidKey[]>;
  count(query?: KeyQuery): Promise<number>;
}

export interface StoreAccess<V> {
  get(key: IDBValidKey): Promise<V | undefined>;
  getAll(query?: KeyQuery, count?: number): Promise<V[]>;
  getAllKeys(query?: KeyQuery, count?: number): Promise<IDBValidKey[]>;
  put(value: V): Promise<void>;
  delete(key: IDBValidKey | KeyRangeSpec): Promise<void>;
  count(query?: KeyQuery): Promise<number>;
  clear(): Promise<void>;
  index(name: string): IndexAccess<V>;
}

export interface Transaction {
  store<N extends StoreName>(name: N): StoreAccess<StoreValueMap[N]>;
}

export type TransactionMode = 'readonly' | 'readwrite';

interface Backend {
  readonly kind: 'indexeddb' | 'memory';
  transact<T>(names: readonly StoreName[], mode: TransactionMode, body: (tx: Transaction) => Promise<T> | T): Promise<T>;
  close(): void;
}

// MARK: - IndexedDB backend

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function wrapIDBStore<V>(store: IDBObjectStore): StoreAccess<V> {
  return {
    get: (key) => requestToPromise(store.get(key) as IDBRequest<V | undefined>),
    getAll: (query, count) => requestToPromise(store.getAll(toIDBQuery(query), count) as IDBRequest<V[]>),
    getAllKeys: (query, count) => requestToPromise(store.getAllKeys(toIDBQuery(query), count)),
    put: async (value) => {
      await requestToPromise(store.put(value));
    },
    delete: async (key) => {
      const query = toIDBQuery(key as KeyQuery);
      if (query === undefined) return;
      await requestToPromise(store.delete(query));
    },
    count: (query) => requestToPromise(store.count(toIDBQuery(query))),
    clear: async () => {
      await requestToPromise(store.clear());
    },
    index: (name) => {
      const index = store.index(name);
      return {
        getAll: (query, count) => requestToPromise(index.getAll(toIDBQuery(query), count) as IDBRequest<V[]>),
        getAllKeys: (query, count) => requestToPromise(index.getAllKeys(toIDBQuery(query), count)),
        count: (query) => requestToPromise(index.count(toIDBQuery(query))),
      };
    },
  };
}

class IndexedDBBackend implements Backend {
  readonly kind = 'indexeddb' as const;
  private readonly database: IDBDatabase;
  private closed = false;

  constructor(database: IDBDatabase) {
    this.database = database;
    // Another tab is upgrading. Letting go immediately is what stops it from blocking; the
    // next `transact` here will fail loudly rather than write against a stale schema.
    database.onversionchange = () => {
      this.close();
      log.warn('closed by a schema upgrade in another tab');
    };
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.database.close();
  }

  async transact<T>(
    names: readonly StoreName[],
    mode: TransactionMode,
    body: (tx: Transaction) => Promise<T> | T,
  ): Promise<T> {
    if (this.closed) throw StorageError.unavailable();

    let transaction: IDBTransaction;
    try {
      transaction = this.database.transaction(names as unknown as string[], mode);
    } catch (cause) {
      throw StorageError.aborted(cause);
    }

    const stores = new Map<string, StoreAccess<unknown>>();
    const access: Transaction = {
      store<N extends StoreName>(name: N): StoreAccess<StoreValueMap[N]> {
        const existing = stores.get(name);
        if (existing !== undefined) return existing as StoreAccess<StoreValueMap[N]>;
        const wrapped = wrapIDBStore<StoreValueMap[N]>(transaction.objectStore(name));
        stores.set(name, wrapped as StoreAccess<unknown>);
        return wrapped;
      },
    };

    const settled = new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error ?? new Error('transaction aborted'));
      transaction.onerror = () => reject(transaction.error ?? new Error('transaction failed'));
    });
    // The rejection is always handled below; attaching a no-op keeps an abort that happens
    // while `body` is still running from surfacing as an unhandled rejection first.
    settled.catch(() => {});

    let result: T;
    try {
      result = await body(access);
    } catch (cause) {
      try {
        transaction.abort();
      } catch {
        // Already finished; the original failure is the one that matters.
      }
      throw translate(cause);
    }

    try {
      await settled;
    } catch (cause) {
      throw translate(cause);
    }
    return result;
  }
}

function translate(cause: unknown): unknown {
  if (isStorageError(cause)) return cause;
  if (isQuotaError(cause)) return StorageError.quotaExceeded(undefined, undefined, cause);
  if (cause instanceof Error && (cause.name === 'TransactionInactiveError' || cause.name === 'InvalidStateError')) {
    return StorageError.aborted(cause);
  }
  return cause;
}

// MARK: - Memory backend

/**
 * A complete, transactional stand-in used when IndexedDB refuses to open. It keeps the app
 * usable for the session — projects open, sounds import, pads play — and the UI tells the
 * user their work will not survive a reload. Rollback is an undo log rather than a copy of
 * the store, so aborting a transaction that touched one record costs one record.
 */
class MemoryBackend implements Backend {
  readonly kind = 'memory' as const;
  private readonly stores = new Map<StoreName, Map<string, unknown>>();
  /** Serialises transactions the way IndexedDB does, so read-modify-write stays correct. */
  private queue: Promise<unknown> = Promise.resolve();

  constructor() {
    for (const spec of SCHEMA) this.stores.set(spec.name, new Map());
  }

  close(): void {
    for (const store of this.stores.values()) store.clear();
  }

  transact<T>(_names: readonly StoreName[], _mode: TransactionMode, body: (tx: Transaction) => Promise<T> | T): Promise<T> {
    const run = async (): Promise<T> => {
      const undo: Array<() => void> = [];
      const access: Transaction = {
        store: <N extends StoreName>(name: N) => this.access<StoreValueMap[N]>(name, undo),
      };
      try {
        return await body(access);
      } catch (cause) {
        for (let index = undo.length - 1; index >= 0; index -= 1) undo[index]?.();
        throw translate(cause);
      }
    };
    const result = this.queue.then(run, run);
    this.queue = result.then(
      () => undefined,
      () => undefined,
    );
    return result;
  }

  private access<V>(name: StoreName, undo: Array<() => void>): StoreAccess<V> {
    const spec = SCHEMA.find((entry) => entry.name === name);
    const map = this.stores.get(name);
    if (spec === undefined || map === undefined) throw StorageError.corrupt(`The “${name}” store`);

    const entries = (): Array<[IDBValidKey, V]> => {
      const collected: Array<[IDBValidKey, V]> = [];
      for (const value of map.values()) {
        const key = extractKey(value, spec.keyPath);
        if (key !== undefined) collected.push([key, value as V]);
      }
      collected.sort((left, right) => compareKeys(left[0], right[0]));
      return collected;
    };

    const remember = (token: string): void => {
      const had = map.has(token);
      const previous = map.get(token);
      undo.push(() => {
        if (had) map.set(token, previous);
        else map.delete(token);
      });
    };

    const byIndex = (indexName: string): Array<[IDBValidKey, V]> => {
      const indexSpec = spec.indexes.find((entry) => entry.name === indexName);
      if (indexSpec === undefined) throw StorageError.corrupt(`The “${indexName}” index`);
      const collected: Array<[IDBValidKey, V]> = [];
      for (const value of map.values()) {
        const key = extractKey(value, indexSpec.keyPath);
        if (key !== undefined) collected.push([key, value as V]);
      }
      collected.sort((left, right) => compareKeys(left[0], right[0]));
      return collected;
    };

    const limit = <T>(items: T[], count?: number): T[] =>
      count === undefined || count <= 0 ? items : items.slice(0, count);

    return {
      get: async (key) => map.get(keyToken(key)) as V | undefined,
      getAll: async (query, count) =>
        limit(
          entries()
            .filter(([key]) => matchesQuery(key, query))
            .map(([, value]) => value),
          count,
        ),
      getAllKeys: async (query, count) =>
        limit(
          entries()
            .filter(([key]) => matchesQuery(key, query))
            .map(([key]) => key),
          count,
        ),
      put: async (value) => {
        const key = extractKey(value, spec.keyPath);
        if (key === undefined) throw StorageError.corrupt(`A record in “${name}”`);
        const token = keyToken(key);
        remember(token);
        map.set(token, value);
      },
      delete: async (key) => {
        if (isRangeSpec(key)) {
          for (const [entryKey] of entries()) {
            if (!matchesQuery(entryKey, key)) continue;
            const token = keyToken(entryKey);
            remember(token);
            map.delete(token);
          }
          return;
        }
        const token = keyToken(key);
        remember(token);
        map.delete(token);
      },
      count: async (query) => entries().filter(([key]) => matchesQuery(key, query)).length,
      clear: async () => {
        for (const [entryKey] of entries()) {
          const token = keyToken(entryKey);
          remember(token);
          map.delete(token);
        }
      },
      index: (indexName) => ({
        getAll: async (query, count) =>
          limit(
            byIndex(indexName)
              .filter(([key]) => matchesQuery(key, query))
              .map(([, value]) => value),
            count,
          ),
        getAllKeys: async (query, count) =>
          limit(
            byIndex(indexName)
              .filter(([key]) => matchesQuery(key, query))
              .map(([key]) => key),
            count,
          ),
        count: async (query) => byIndex(indexName).filter(([key]) => matchesQuery(key, query)).length,
      }),
    };
  }
}

// MARK: - Opening

/** How long to wait for `open` before deciding this browser is not going to co-operate. */
const OPEN_TIMEOUT_MS = 8_000;

let backendPromise: Promise<Backend> | null = null;
let activeBackend: Backend | null = null;

function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || indexedDB === null) {
      reject(StorageError.unavailable());
      return;
    }

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (cause) {
      reject(StorageError.unavailable(cause));
      return;
    }

    // Safari has shipped builds where `open` neither succeeds nor errors if the storage
    // daemon is wedged. A timeout turns an app that never launches into an app that
    // launches in memory mode and says so.
    const timer = setTimeout(() => reject(StorageError.unavailable()), OPEN_TIMEOUT_MS);
    const finish = (): void => clearTimeout(timer);

    // The event carries `oldVersion`, which is the only reliable way to know which rungs of
    // the ladder still have to run.
    request.onupgradeneeded = (event) => {
      applyUpgrade(request.result, event.oldVersion);
    };
    request.onblocked = () => {
      finish();
      reject(StorageError.blocked());
    };
    request.onsuccess = () => {
      finish();
      resolve(request.result);
    };
    request.onerror = () => {
      finish();
      reject(StorageError.unavailable(request.error));
    };
  });
}

async function resolveBackend(): Promise<Backend> {
  try {
    const database = await openIndexedDB();
    const backend = new IndexedDBBackend(database);
    activeBackend = backend;
    log.info('opened', { store: idPrefix('indexeddb'), version: DATABASE_VERSION });
    return backend;
  } catch (cause) {
    const backend = new MemoryBackend();
    activeBackend = backend;
    log.error('IndexedDB unavailable; running in memory for this session', cause);
    return backend;
  }
}

/** Opens the database once and hands the same connection to every caller. */
export function openDatabase(): Promise<Backend> {
  backendPromise ??= resolveBackend();
  return backendPromise;
}

/**
 * Which backend is live. `'memory'` means nothing survives a reload and the UI must say so —
 * it is a capability-matrix row, not a detail.
 */
export function databaseBackend(): 'indexeddb' | 'memory' | 'unopened' {
  return activeBackend?.kind ?? 'unopened';
}

/** The one way to touch storage. See the note at the top of the file about `await`. */
export async function transact<T>(
  names: readonly StoreName[],
  mode: TransactionMode,
  body: (tx: Transaction) => Promise<T> | T,
): Promise<T> {
  const backend = await openDatabase();
  return backend.transact(names, mode, body);
}

/** Convenience for the overwhelmingly common single-store read. */
export async function readStore<N extends StoreName, T>(
  name: N,
  body: (store: StoreAccess<StoreValueMap[N]>) => Promise<T> | T,
): Promise<T> {
  return transact([name], 'readonly', (tx) => body(tx.store(name)));
}

/** Convenience for the equally common single-store write. */
export async function writeStore<N extends StoreName, T>(
  name: N,
  body: (store: StoreAccess<StoreValueMap[N]>) => Promise<T> | T,
): Promise<T> {
  return transact([name], 'readwrite', (tx) => body(tx.store(name)));
}

/** Closes the connection. Used by tests and by the "delete all data" flow. */
export function closeDatabase(): void {
  activeBackend?.close();
  activeBackend = null;
  backendPromise = null;
}

// MARK: - Settings

/**
 * Small typed key/value storage for anything that is not a project: the last opened project,
 * whether the install tip has been shown, whether persistence was already requested.
 */
export async function readSetting<T>(key: string, fallback: T): Promise<T> {
  const record = await readStore('settings', (store) => store.get(key));
  return record === undefined ? fallback : (record.value as T);
}

export async function writeSetting(key: string, value: unknown): Promise<void> {
  await writeStore('settings', (store) => store.put({ key, value }));
}

export async function deleteSetting(key: string): Promise<void> {
  await writeStore('settings', (store) => store.delete(key));
}

// MARK: - Persistence and quota

export interface PersistenceState {
  /** True when the browser has promised not to evict this origin under storage pressure. */
  persisted: boolean;
  /** False when `navigator.storage.persist` does not exist at all. */
  supported: boolean;
  /** True when this call is what asked for it, rather than reading an existing grant. */
  requested: boolean;
}

const PERSIST_REQUESTED_KEY = 'storage.persistRequested';
let persistencePromise: Promise<PersistenceState> | null = null;

/**
 * Asks the browser to keep PadLab's data. **Call this from inside a user gesture** — Chrome
 * may show a permission prompt, and a prompt raised outside a gesture is refused outright.
 * Safari grants persistence on its own engagement heuristics and never prompts, so the call
 * is harmless there; what matters is that we ask exactly once and remember that we did, so a
 * user who declined is not asked again on every launch.
 */
export function requestPersistentStorage(): Promise<PersistenceState> {
  persistencePromise ??= (async (): Promise<PersistenceState> => {
    const storage = navigator.storage as StorageManager | undefined;
    if (storage === undefined || typeof storage.persist !== 'function' || typeof storage.persisted !== 'function') {
      return { persisted: false, supported: false, requested: false };
    }

    try {
      if (await storage.persisted()) {
        return { persisted: true, supported: true, requested: false };
      }
      const alreadyAsked = await readSetting(PERSIST_REQUESTED_KEY, false);
      if (alreadyAsked) {
        return { persisted: false, supported: true, requested: false };
      }
      const granted = await storage.persist();
      await writeSetting(PERSIST_REQUESTED_KEY, true);
      log.info('persistent storage requested', { granted });
      return { persisted: granted, supported: true, requested: true };
    } catch (cause) {
      log.error('persistent storage request failed', cause);
      return { persisted: false, supported: true, requested: false };
    }
  })();
  return persistencePromise;
}

/** Reads the grant without asking for it. Safe anywhere, including at launch. */
export async function isStoragePersisted(): Promise<boolean> {
  const storage = navigator.storage as StorageManager | undefined;
  if (storage === undefined || typeof storage.persisted !== 'function') return false;
  try {
    return await storage.persisted();
  } catch {
    return false;
  }
}

export interface StorageEstimate {
  supported: boolean;
  /** Bytes this origin is currently using, as the browser accounts for it. */
  usage: number;
  /** Bytes the browser is prepared to let this origin use. */
  quota: number;
  /** Per-mechanism breakdown where the browser exposes one; WebKit does not. */
  details: Readonly<Record<string, number>>;
  persisted: boolean;
}

/**
 * What the storage screen shows. Numbers here are the browser's, not ours: Safari rounds
 * `quota` heavily and reports usage in padded buckets to frustrate cross-site fingerprinting,
 * so this is for orientation, and the per-project figures the store computes are the ones to
 * trust for "what will clearing this reclaim?".
 */
export async function storageEstimate(): Promise<StorageEstimate> {
  const storage = navigator.storage as StorageManager | undefined;
  const persisted = await isStoragePersisted();
  if (storage === undefined || typeof storage.estimate !== 'function') {
    return { supported: false, usage: 0, quota: 0, details: {}, persisted };
  }
  try {
    const estimate = await storage.estimate();
    const raw = (estimate as { usageDetails?: Record<string, number> }).usageDetails ?? {};
    const details: Record<string, number> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value === 'number' && Number.isFinite(value)) details[key] = value;
    }
    return {
      supported: true,
      usage: estimate.usage ?? 0,
      quota: estimate.quota ?? 0,
      details,
      persisted,
    };
  } catch {
    return { supported: false, usage: 0, quota: 0, details: {}, persisted };
  }
}

/**
 * Guards a write that is about to happen. Returns nothing when there is room, throws the
 * quota error with real numbers when there is not — which is a far better message than the
 * one the browser produces halfway through a 200 MB import.
 */
export async function requireSpace(neededBytes: number): Promise<void> {
  if (!Number.isFinite(neededBytes) || neededBytes <= 0) return;
  const estimate = await storageEstimate();
  if (!estimate.supported || estimate.quota <= 0) return;
  const available = Math.max(0, estimate.quota - estimate.usage);
  // Headroom for the peaks cache and the project rewrite that follow every import, matching
  // the native store's one-eighth-plus-4 MB rule.
  const needed = neededBytes + Math.floor(neededBytes / 8) + 4 * 1024 * 1024;
  if (available < needed) throw StorageError.quotaExceeded(needed, available);
}
